import { Component, OnDestroy, OnInit } from '@angular/core';
import { BookMetadataV2, Stage } from '../../model/book-model';
import { ActivatedRoute, Router } from '@angular/router';
import { LangCode } from 'src/app/utils/text-compare';
import { OffcanvasMenuService } from 'src/app/services/offcanvas-menu.service';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { BookReaderService } from '../../services/book-reader.service';
import { BookReaderApiV3Service } from '../../services/v3/book-reader-api-v3.service';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { CourseFilter } from 'src/app/model/CourseFilters';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import { V3DrmProduct } from '../../model/v3/drm';

@Component({
  selector: 'app-book-reader',
  templateUrl: './book-reader.component.html',
  styleUrls: ['./book-reader.component.scss'],
  standalone: false
})
export class BookReaderComponent implements OnInit, OnDestroy {

  product: V3DrmProduct
  ebookId: number
  isEbookLoaded: boolean

  // Content version of the opened book (from metadata "ver"); decides which reader component renders.
  ver: number
  // Metadata cast to the concrete per-version type.
  metadataV1: Stage
  metadataV2: BookMetadataV2

  constructor(
    private offcanvasMenuService: OffcanvasMenuService,
    private bookReaderService: BookReaderService,
    private router: Router,
    private route: ActivatedRoute,
    private apiProvider: NativeServiceApiProvider,
    private spinner: SpinnerService,
    private bookReaderApi: BookReaderApiV3Service
  ) {}

  ngOnInit(): void {
    this.offcanvasMenuService.setMenu('exerciseOptions')
    this.route.paramMap.pipe(
      tap(params => {
        this.ebookId = +params.get("ebookId");
      }),
      switchMap(_ => this.getEBook()))
      .subscribe()
  }

  ngOnDestroy(): void {
    this.offcanvasMenuService.setMenu("defaultHamburger")
  }

  getAccent() {
    return `stg-${this.product?.name.slice(this.product.name.length - 2).trim()}`
  }

  getEbookName() {
    return this.product?.name
  }

  getTextSizeLevel(){
    return this.bookReaderService.getLevel()
  }

  setTextSize(value: number){
    this.bookReaderService.setSize(value)
  }

  getEBook(){
    return this.spinner.trace<V3DrmProduct>(this.loadProduct().pipe(
      tap(product => {
            if(product) this.product = product
            this.isEbookLoaded = true
      }),
      switchMap(product => this.detectVersion().pipe(map(() => product)))
    ))
  }

  private loadProduct(): Observable<V3DrmProduct> {
    return this.apiProvider.booksV3().pipe(
      switchMap(api => api.getProductById(this.ebookId)),
      map(entry => entry?.share.product ?? null),
      catchError(_ => of(null))
    )
  }

  // Reads the book metadata once, sets `ver` and casts the metadata to the concrete per-version type.
  private detectVersion(){
    if(!this.product?.path) {
      this.ver = 1
      return of(null)
    }
    return this.bookReaderApi.getMetadata(this.product.path).pipe(
      tap(metadata => {
        this.ver = metadata?.ver ?? 1
        if(this.ver === 2) this.metadataV2 = metadata as BookMetadataV2
        else this.metadataV1 = metadata as Stage
      }),
      catchError(_ => {
        this.ver = 1
        return of(null)
      })
    )
  }

  getEbookCategory() {
    if(this.product?.categories.length === 1) return this.product.categories[0]
    return this.product?.categories.filter(category =>
      category !== 'English' && category !== 'Teacher')[0]
  }

  getLanguageFilter(){
    let lang = this.getEbookCategory()
    if(lang) return CourseFilter.filters.find(filter => filter.category == lang).filter
    else return ''
  }

  goToEbooksListWithFilter(language: string) {
    this.router.navigate(
      ['../../'],
      {
        relativeTo: this.route,
        queryParams: { language: `${language}`},
        queryParamsHandling: 'merge'
      }
    )
  }

  getEbookLanguageTranslation() {
    return this.product?.categories ? 'EBOOKS.FILTER_LANGS.' + LangCode[this.product.categories[0]] : ''
  }

}
